import Alert from "@mui/material/Alert";
import dayjs from "dayjs";
import * as React from "react";
import { Link } from "react-router-dom";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { UserLink } from "@/shared/components";
import { DeviceLink } from "@/shared/components/DeviceLink";
import {
  ResourceView,
  filterToCondition,
  useResourceView,
  type FilterDefinition,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import { TagList } from "@/shared/components/TagList";
import { DatasetRecord } from "@/shared/domain/datasets/DatasetRecord";
import {
  datasetQueryKeys,
  useDatasetMetadataKeys,
} from "@/shared/domain/datasets/hooks";
import {
  computeMetadataRows,
  formatMetadataValue,
  projectRows,
  QueryContentMode,
  QueryTarget,
  type EntityIdGetter,
  type MetadataKey,
  type Row,
} from "@/shared/domain/query";
import {
  useMetadataValues,
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useDatasetTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  buildColumnsForPreferences,
  cellStyles,
  colId,
  createColumnHelper,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation/Goto";
import { RobotoPrincipal, RobotoPrincipalType } from "@/shared/principal";
import type { Condition, SearchQueryBody, SortDirection } from "@/types";

import styles from "./DatasetsList.module.css";

export type DatasetRow = Row<DatasetRecord>;

const getDatasetId: EntityIdGetter<DatasetRecord> = (d) => d.dataset_id;

const columnHelper = createColumnHelper<DatasetRow>();

const STATIC_DATASET_COLUMNS: ColDef<DatasetRow>[] = [
  columnHelper.accessor("dataset_id", {
    headerName: "ID",
    width: 150,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<DatasetRow, string>) => {
      const id = p.value;
      if (!id) {
        return null;
      }
      return (
        <Link to={`${Goto.route.Datasets}/${id}`} className={styles.nameLink}>
          {id}
        </Link>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    sortable: true,
  }),
  columnHelper.accessor("created", {
    headerName: "Created",
    width: 150,
    sortable: true,
    valueFormatter: (p) => {
      if (!p.value) {
        return "";
      }
      const value = dayjs(p.value);
      return dayjs().diff(value, "day") >= 7
        ? value.format("MMM D, YYYY")
        : value.fromNow();
    },
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    width: 150,
    sortable: true,
    cellRenderer: CreatedByCell,
  }),
  columnHelper.accessor("modified", {
    headerName: "Modified",
    width: 150,
    sortable: true,
    valueFormatter: (p) => {
      if (!p.value) {
        return "";
      }
      const value = dayjs(p.value);
      return dayjs().diff(value, "day") >= 7
        ? value.format("MMM D, YYYY")
        : value.fromNow();
    },
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    width: 150,
    sortable: true,
    cellRenderer: CreatedByCell,
  }),
  columnHelper.accessor("org_id", {
    headerName: "Org",
    width: 150,
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: (p: ICellRendererParams<DatasetRow, string[]>) => (
      <TagList tags={p.value ?? []} maxDisplayCount={3} />
    ),
  }),
  columnHelper.accessor("device_id", {
    headerName: "Device",
    width: 150,
  }),
  columnHelper.accessor("storage_location", {
    headerName: "Storage Location",
    width: 140,
  }),
  columnHelper.accessor("administrator", {
    headerName: "Administrator",
    width: 140,
  }),
  {
    colId: "source",
    headerName: "Source",
    width: 150,
    valueGetter: (p: { data?: DatasetRow }) =>
      p.data?.device_id ?? p.data?.created_by ?? "",
    cellRenderer: SourceCell,
  },
];

function buildMetadataColumn(metadataKey: string): ColDef<DatasetRow> {
  const id: MetadataKey = `metadata.${metadataKey}`;
  return {
    colId: id,
    headerName: metadataKey,
    valueGetter: (p) => p.data?.[id],
    valueFormatter: (p) => formatMetadataValue(p.value),
    width: 160,
    // Dataset metadata is never sortable
    sortable: false,
  };
}

const DEFAULT_VISIBLE_IDS = [
  "dataset_id",
  "name",
  "created",
  "created_by",
  "modified",
  "modified_by",
  "org_id",
  "tags",
];

const STATIC_PICKER_OPTIONS: ColumnPickerOption[] = STATIC_DATASET_COLUMNS.map(
  (c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }),
);

interface DatasetsListProps {
  /** Parent-provided scoping conditions (e.g. a collection filter).
   *  ANDed with any user-driven table filters. */
  conditions?: Condition[];
  /** Bulk-action options surfaced in the DataTable.Selection menu. */
  selectionOptions?: SelectionOption<DatasetRow>[];
}

export function DatasetsList({
  conditions,
  selectionOptions,
}: DatasetsListProps = {}) {
  const { currentOrganization } = useAuth();
  const { datasetService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useDatasetTablePreferences();

  const metadataKeysQuery = useDatasetMetadataKeys(resolvedOrgId);

  // Capture initial columns preferences once at mount.
  const [initialColumnIds] = React.useState(() => preferences.columns);

  // TODO(custom-fields): concat another `dynamicColumns` source when the
  // backend / frontend hook lands.
  const columns = React.useMemo<ColDef<DatasetRow>[]>(
    () =>
      buildColumnsForPreferences<DatasetRow>({
        staticColumns: STATIC_DATASET_COLUMNS,
        dynamicColumns: (metadataKeysQuery.data ?? []).map(buildMetadataColumn),
        preferenceOrder: initialColumnIds,
      }),
    [initialColumnIds, metadataKeysQuery.data],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_PICKER_OPTIONS,
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [metadataKeysQuery.data],
  );

  const view = useResourceView<DatasetRow>({
    getRowId: getDatasetId,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      { type: "string", field: "dataset_id", name: "Dataset Id" },
      { type: "string", field: "name", name: "Name" },
      { type: "string", field: "description", name: "Description" },
      { type: "date", field: "created", name: "Created" },
      { type: "string", field: "created_by", name: "Created By" },
      { type: "date", field: "modified", name: "Modified" },
      { type: "string", field: "modified_by", name: "Modified By" },
      {
        type: "enum",
        field: "tags",
        name: "Tags",
        options: function getDatasetsTags() {
          return datasetService.getTagsForOrg({
            resourceOwnerId: resolvedOrgId ?? "",
          });
        },
      },
    ],
    [datasetService, resolvedOrgId],
  );

  const filterCondition = filterToCondition(view.filters, {
    matchMode: view.matchMode,
  });

  // AND the parent-provided scoping conditions with any user-driven filters.
  const allConditions: Condition[] = [
    ...(conditions ?? []),
    ...(filterCondition ? [filterCondition] : []),
  ];
  let condition: Condition | undefined;
  if (allConditions.length === 1) {
    condition = allConditions[0];
  } else if (allConditions.length > 1) {
    condition = { conditions: allConditions, operator: "AND" as const };
  }

  const query: SearchQueryBody = {
    ...(view.table.sort && {
      sort_by: view.table.sort.columnId,
      sort_direction: view.table.sort.direction.toUpperCase() as SortDirection,
    }),
    ...(condition && { condition }),
  };

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Datasets,
    contentMode: QueryContentMode.RecordOnly,
    queryKey: datasetQueryKeys.list(resolvedOrgId, query),
  });

  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<DatasetRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );

  const countQuery = useQueryCount(queryId);

  const datasets: DatasetRecord[] = React.useMemo(
    () => pageQuery.data ?? [],
    [pageQuery.data],
  );

  const visibleMetadataKeys = React.useMemo<MetadataKey[]>(() => {
    const out: MetadataKey[] = [];
    for (const id of view.table.visibleColumnIds) {
      if (id.startsWith("metadata.")) {
        out.push(id as MetadataKey);
      }
    }
    return out;
  }, [view.table.visibleColumnIds]);

  const { getMetadataValue } = useMetadataValues({
    target: QueryTarget.Datasets,
    metadataColumns: visibleMetadataKeys,
    entityIds: datasets.map(getDatasetId),
  });

  const metadataByDataset = React.useMemo(
    () =>
      computeMetadataRows(
        datasets,
        visibleMetadataKeys,
        getMetadataValue,
        getDatasetId,
      ),
    [datasets, visibleMetadataKeys, getMetadataValue],
  );

  const rows = React.useMemo<DatasetRow[]>(
    () => projectRows(datasets, metadataByDataset, getDatasetId),
    [datasets, metadataByDataset],
  );

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.addFilter}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          {hasSelectionOptions && (
            <ResourceView.Selection<DatasetRow>
              selection={view.table.selection}
              clearSelection={view.table.clearSelection}
              options={selectionOptions ?? []}
            />
          )}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          matchMode={view.matchMode}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.addFilter}
        />
        <DataTable<DatasetRow>
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No datasets"
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
        />
      </ResourceView.Root>
    </>
  );
}

function SourceCell(params: ICellRendererParams<DatasetRow, string>) {
  const row = params.data;
  if (row?.device_id) {
    return (
      <DeviceLink
        deviceId={row.device_id}
        linkSx={{ fontSize: "var(--font-size-xs)" }}
      />
    );
  }
  return <CreatedByCell {...params} value={row?.created_by ?? ""} />;
}

function CreatedByCell(params: ICellRendererParams<DatasetRow, string>) {
  const value = params.value ?? "";
  const principal = RobotoPrincipal.fromString(value);
  if (principal?.ptype === RobotoPrincipalType.User) {
    return (
      <UserLink
        userId={principal.unqualifiedId}
        linkSx={{ fontSize: "var(--font-size-xs)" }}
      />
    );
  }
  return <span>{principal?.unqualifiedId ?? value}</span>;
}
